import React, { useEffect, useMemo, useState } from 'react';
import '../styles/Translate.css';

const NATIVE_NAME_MAP = {
  af: 'Afrikaans',
  sq: 'Shqip',
  ar: 'العربية',
  hy: 'Հայերեն',
  az: 'Azərbaycanca',
  eu: 'Euskara',
  be: 'Беларуская',
  bn: 'বাংলা',
  bs: 'Bosanski',
  bg: 'Български',
  ca: 'Català',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  hr: 'Hrvatski',
  cs: 'Čeština',
  da: 'Dansk',
  nl: 'Nederlands',
  en: 'English',
  et: 'Eesti',
  fi: 'Suomi',
  fr: 'Français',
  de: 'Deutsch',
  el: 'Ελληνικά',
  gu: 'ગુજરાતી',
  he: 'עברית',
  hi: 'हिंदी',
  hu: 'Magyar',
  id: 'Bahasa Indonesia',
  it: 'Italiano',
  ja: '日本語',
  kn: 'ಕನ್ನಡ',
  ko: '한국어',
  lv: 'Latviešu',
  lt: 'Lietuvių',
  ml: 'മലയാളം',
  mr: 'मराठी',
  ne: 'नेपाली',
  no: 'Norsk',
  or: 'ଓଡ଼ିଆ',
  pa: 'ਪੰਜਾਬੀ',
  pl: 'Polski',
  pt: 'Português',
  ro: 'Română',
  ru: 'Русский',
  sa: 'संस्कृतम्',
  sr: 'Српски',
  sk: 'Slovenčina',
  sl: 'Slovenščina',
  es: 'Español',
  sw: 'Kiswahili',
  sv: 'Svenska',
  ta: 'தமிழ்',
  te: 'తెలుగు',
  th: 'ไทย',
  tr: 'Türkçe',
  uk: 'Українська',
  ur: 'اردو',
  vi: 'Tiếng Việt',
};

const FALLBACK_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
];

function LanguageSettings({ onBack }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem('chatapp-language') || 'en'
  );
  const [status, setStatus] = useState('Loading languages...');

  const setGoogTransCookie = (lang) => {
    try {
      if (!lang || lang === 'en') {
        const expires = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
        document.cookie = `googtrans=; path=/; ${expires};`;
        if (window.location.hostname.includes('.')) {
          document.cookie = `googtrans=; domain=.${window.location.hostname}; path=/; ${expires};`;
        }
        return;
      }
      const pair = `/en/${lang}`;
      document.cookie = `googtrans=${pair}; path=/;`;
      if (window.location.hostname.includes('.')) {
        document.cookie = `googtrans=${pair}; domain=.${window.location.hostname}; path=/;`;
      }
    } catch (_) {
      // ignore cookie failures
    }
  };

  const applyLanguage = (languageCode) => {
    const code = languageCode || 'en';
    setSelectedLanguage(code);
    localStorage.setItem('chatapp-language', code);
    setGoogTransCookie(code);

    const select = document.querySelector('.goog-te-combo');
    if (select) {
      select.value = code;
      select.dispatchEvent(new Event('change'));
      setStatus(`Applied language: ${code}`);
    } else {
      setStatus('Applying language...');
      setTimeout(() => window.location.reload(), 200);
    }
  };

  useEffect(() => {
    const previousOnError = window.onerror;

    // Hard guard for CRA runtime overlay: ignore known cross-origin Google
    // Translate script errors so dev overlay does not break the page.
    window.onerror = function (message, source, lineno, colno, error) {
      const msg = (message || '').toString();
      const src = (source || '').toString();
      if (
        msg === 'Script error.' ||
        src.includes('translate.googleapis.com') ||
        src.includes('/translate_http/')
      ) {
        return true; // mark handled
      }
      if (typeof previousOnError === 'function') {
        return previousOnError(message, source, lineno, colno, error);
      }
      return false;
    };

    const suppressTranslateScriptError = (event) => {
      const filename = event?.filename || '';
      const message = event?.message || '';
      if (
        filename.includes('translate.googleapis.com') ||
        filename.includes('/translate_http/') ||
        message === 'Script error.'
      ) {
        event.preventDefault();
        return true;
      }
      return false;
    };

    const suppressUnhandledRejection = (event) => {
      const reasonText = String(event?.reason || '');
      if (
        reasonText.includes('translate.googleapis.com') ||
        reasonText.includes('/translate_http/') ||
        reasonText.includes('Script error')
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('error', suppressTranslateScriptError, true);
    window.addEventListener('unhandledrejection', suppressUnhandledRejection, true);

    // Define global init function before script loads
    window.googleTranslateElementInit = function () {
      if (window.google && window.google.translate) {
        const root = document.getElementById('google_translate_element');
        if (!root) return;
        // Avoid duplicate initialization on same mount
        if (root.getAttribute('data-initialized') === 'true') {
          setStatus('Google Translate ready');
          return;
        }

        new window.google.translate.TranslateElement(
          { pageLanguage: 'en' },
          'google_translate_element'
        );
        root.setAttribute('data-initialized', 'true');
        setStatus('Google Translate ready');
      }
    };

    // Add script only if not already present
    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    } else {
      // If script already present, re-init
      if (window.google && window.google.translate) {
        window.googleTranslateElementInit();
      }
    }

    // Extract language options from Google dropdown
    const readOptionsInterval = setInterval(() => {
      const select = document.querySelector('.goog-te-combo');
      if (!select) return;
      const opts = Array.from(select.options || [])
        .map((opt) => ({
          code: opt.value,
          name: opt.text,
          nativeName: NATIVE_NAME_MAP[opt.value] || opt.text,
        }))
        .filter((lang) => !!lang.code);
      if (opts.length > 0) {
        const hasEnglish = opts.some((l) => l.code === 'en');
        const merged = hasEnglish ? opts : [...FALLBACK_LANGUAGES, ...opts];
        setLanguages(merged);
        setStatus('');
        clearInterval(readOptionsInterval);
      }
    }, 200);

    // Cleanup
    return () => {
      clearInterval(readOptionsInterval);
      window.removeEventListener('error', suppressTranslateScriptError, true);
      window.removeEventListener('unhandledrejection', suppressUnhandledRejection, true);
      window.onerror = previousOnError;
    };
  }, []);

  const filteredLanguages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return languages;
    return languages.filter(
      (lang) =>
        lang.name.toLowerCase().includes(q) ||
        lang.nativeName.toLowerCase().includes(q) ||
        lang.code.toLowerCase().includes(q)
    );
  }, [searchQuery, languages]);

  return (
    <div className="language-settings-container">
      <div className="language-modal-card notranslate" translate="no">
        <div className="language-modal-header">
          <h2 className="notranslate">Select Language</h2>
          <button className="language-close-btn" onClick={onBack} aria-label="Close language picker">
            ×
          </button>
        </div>

        <div className="language-modal-body">
          <input
            type="text"
            className="language-search-input"
            placeholder="Search languages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="language-grid">
            {filteredLanguages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`language-tile ${selectedLanguage === lang.code ? 'active' : ''}`}
                onClick={() => applyLanguage(lang.code)}
              >
                <div className="language-tile-name notranslate">{lang.name}</div>
                <div className="language-tile-native notranslate">{lang.nativeName}</div>
              </button>
            ))}
          </div>

          {filteredLanguages.length === 0 ? (
            <div className="language-status-line">No languages found for your search.</div>
          ) : null}

          {status ? <div className="language-status-line">{status}</div> : null}
        </div>

        {/* Hidden Google translate widget (functional source) */}
        <div id="google_translate_element" className="google-translate-box"></div>
      </div>
    </div>
  );
}

export default LanguageSettings;